import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { listThreads } from '../../services/copilot/copilot.service';
import { CopilotThread } from '../../models/copilot.model';

interface Props {
  onSelectThread: (threadId: string) => void;
  onNewConversation: () => void;
}

function relativeDate(iso: string, langCode: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return langCode === 'fr' ? "A l'instant" : 'Just now'
    if (diffMin < 60) return `${diffMin} min`
    const diffHours = Math.floor(diffMin / 60)
    if (diffHours < 24) return `${diffHours}h`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d`
    return d.toLocaleDateString(langCode, { day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return ''
  }
}

function threadTitle(thread: CopilotThread, langCode: string): string {
  if (thread.title) return thread.title
  try {
    const d = new Date(thread.createdAt)
    return d.toLocaleDateString(langCode, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return '—'
  }
}

const ThreadList: React.FC<Props> = ({ onSelectThread, onNewConversation }) => {
  const { lang } = StateLanguage()
  const [threads, setThreads] = useState<CopilotThread[] | null>(null)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    listThreads()
      .then(setThreads)
      .catch(() => setThreads([]))
  }, [])

  const filtered = (threads ?? []).filter(t =>
    !filter || threadTitle(t, lang.code).toLowerCase().includes(filter.toLowerCase())
  )

  return (
    <div className="d-flex flex-column" style={{ minHeight: 0, flex: '1 1 0' }}>
      {/* New conversation button */}
      <div className="px-5 pt-4 pb-2">
        <button
          type="button"
          className="btn btn-sm btn-primary w-100"
          onClick={onNewConversation}
        >
          <span className="svg-icon svg-icon-3 me-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="currentColor" />
              <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="currentColor" />
              <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="currentColor" />
            </svg>
          </span>
          {lang.labels.copilotNewConversation}
        </button>
      </div>

      {/* Filter */}
      <div className="px-5 pb-2">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={lang.labels.copilotSearchPlaceholder}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {/* Thread list */}
      <div className="scroll-y px-5 pb-4" style={{ minHeight: 0, flex: '1 1 0' }}>
        {threads === null ? (
          <div className="text-center py-10">
            <span className="spinner-border spinner-border-sm text-primary"></span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-muted text-center fs-7 py-10">{lang.labels.copilotNoThreads}</div>
        ) : (
          filtered.map(t => {
            const attachCount = t.attachmentRefs?.length ?? 0
            return (
              <div
                key={t.id}
                className="d-flex align-items-start justify-content-between py-3 px-3 rounded cursor-pointer bg-hover-light-primary mb-1"
                onClick={() => onSelectThread(t.id)}
              >
                <div className="d-flex flex-column me-2" style={{ minWidth: 0 }}>
                  <span className="fs-7 fw-bold text-gray-800 text-truncate">{threadTitle(t, lang.code)}</span>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <span className="fs-9 text-muted">{relativeDate(t.updatedAt, lang.code)}</span>
                    {attachCount > 0 && (
                      <span className="badge badge-sm badge-light fs-9">
                        <span className="svg-icon svg-icon-9 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="currentColor" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="currentColor" />
                          </svg>
                        </span>
                        {attachCount}
                      </span>
                    )}
                  </div>
                </div>
                <span className="svg-icon svg-icon-5 svg-icon-muted flex-shrink-0 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12.6343 12.5657L8.45001 16.75C8.0358 17.1642 8.0358 17.8358 8.45001 18.25C8.86423 18.6642 9.5358 18.6642 9.95001 18.25L15.4929 12.7071C15.8834 12.3166 15.8834 11.6834 15.4929 11.2929L9.95001 5.75C9.5358 5.33579 8.86423 5.33579 8.45001 5.75C8.0358 6.16421 8.0358 6.83579 8.45001 7.25L12.6343 11.4343C12.9467 11.7467 12.9467 12.2533 12.6343 12.5657Z" fill="currentColor" />
                  </svg>
                </span>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default ThreadList;
